import { useState } from "react";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Mail, Book, Send, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const faqs = [
  {
    question: "Como começo a usar o Publee?",
    answer:
      "Moleza: cria uma conta, ganha 1 crédito grátis, e já pode criar sua primeira publi. Upload da foto, nome do produto, clica em gerar. Em 5 minutos você tem 3 vídeos prontos.",
  },
  {
    question: "Que tipo de foto posso usar?",
    answer:
      "JPG, PNG e WebP. Quanto melhor a foto, melhor o resultado. Mínimo 1080x1080px, máximo 10MB. Aquela foto do iPhone já serve.",
  },
  {
    question: "Como funciona o esquema de créditos?",
    answer:
      "1 crédito = 1 task = 3 publis diferentes. Você pode comprar avulso (sem validade) ou assinar um plano mensal/anual com créditos renovados todo mês.",
  },
  {
    question: "Por quanto tempo as publis ficam disponíveis?",
    answer:
      "7 dias depois de prontas. Baixa logo porque depois some. A gente avisa antes, relaxa.",
  },
  {
    question: "E se der erro no meio do caminho?",
    answer:
      "Pode reprocessar de graça. O sistema continua de onde parou, sem gastar crédito extra. Não somos vilões.",
  },
  {
    question: "Dá pra pedir reembolso?",
    answer:
      "Sim, até 7 dias depois da compra. Tem um limite de uso diário pra garantir que todo mundo jogue limpo.",
  },
  {
    question: "Os créditos avulsos vencem?",
    answer:
      "Não! Crédito avulso é seu pra sempre. Só os de assinatura renovam mensalmente.",
  },
  {
    question: "Como falo com vocês?",
    answer:
      "Formulário aqui embaixo ou email pra suporte@publee.art. Respondemos em até 24h úteis (normalmente bem antes).",
  },
];

const supportChannels = [
  {
    icon: Mail,
    title: "Email",
    description: "Resposta em até 24h",
    action: "suporte@publee.art",
    href: "mailto:suporte@publee.art",
  },
  {
    icon: Book,
    title: "Central de Ajuda",
    description: "FAQs e tutoriais",
    action: "Ver abaixo",
    href: "#faqs",
  },
];

export default function Suporte() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    toast({
      title: "Mensagem enviada! ✉️",
      description: "Responderemos em até 24 horas.",
    });
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--yellow)/0.1),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Central de <span className="text-gradient">Suporte</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Tá com dúvida? A gente resolve. Pode ser coisa besta ou complexa, estamos aqui.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {supportChannels.map((channel, index) => (
              <motion.a
                key={channel.title}
                href={channel.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl gradient-card border border-border hover:border-primary/30 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <channel.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-1">{channel.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {channel.description}
                </p>
                <span className="text-sm text-primary font-medium">
                  {channel.action}
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              Perguntas <span className="text-gradient">Frequentes</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-secondary/50 border border-border rounded-xl px-6 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-medium">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-4">
                Fala com a <span className="text-gradient">gente</span>
              </h2>
              <p className="text-muted-foreground">
                Não achou o que precisava? Manda mensagem que a gente responde.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {isSubmitted ? (
                <div className="text-center p-12 rounded-2xl gradient-card border border-border">
                  <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">
                    Mensagem Enviada!
                  </h3>
                  <p className="text-muted-foreground">
                    Valeu pelo contato. Respondemos em até 24 horas.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 p-8 rounded-2xl gradient-card border border-border"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium mb-2"
                      >
                        Nome
                      </label>
                      <Input
                        id="name"
                        placeholder="Seu nome"
                        required
                        className="bg-secondary border-border"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium mb-2"
                      >
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        required
                        className="bg-secondary border-border"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium mb-2"
                    >
                      Assunto
                    </label>
                    <Input
                      id="subject"
                      placeholder="Qual é a boa?"
                      required
                      className="bg-secondary border-border"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium mb-2"
                    >
                      Mensagem
                    </label>
                    <Textarea
                      id="message"
                      placeholder="Conta tudo pra gente..."
                      rows={5}
                      required
                      className="bg-secondary border-border resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Enviando..."
                    ) : (
                      <>
                        Enviar Mensagem
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
